// FILE: src/support/TicketDetail.jsx
// Show issue/status + message thread + reply box

import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { auth, db } from "../lib/firebase";
import "../styles/layout.css";

export default function TicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [authReady, setAuthReady] = useState(false);
  const [me, setMe] = useState(null);

  const [ticket, setTicket] = useState(null);
  const [ticketColl, setTicketColl] = useState("tickets");
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadErr, setLoadErr] = useState("");

  const scrollerRef = useRef(null);

  function normalizeStatusKey(t) {
    const raw = String(t?.status || "").toLowerCase();
    let k = raw;
    if (k === "live") k = "open";
    if (k === "solved") k = "closed";
    if (k === "pending" || k === "replied") k = "awaiting_customer";
    if (!["open", "awaiting_support", "awaiting_customer", "closed"].includes(k)) k = "open";

    // If older tickets only have open/closed, infer the awaiting state from last reply role.
    if (k === "open") {
      const r = String(t?.lastReplyRole || "").toLowerCase();
      if (r === "staff" || r === "support" || r === "admin") return "awaiting_customer";
      if (r === "user") return "awaiting_support";
    }
    return k;
  }

  function statusLabel(k) {
    if (k === "awaiting_support") return "Awaiting Support";
    if (k === "awaiting_customer") return "Awaiting Customer";
    if (k === "closed") return "Closed";
    return "Open";
  }

  function bestTs(t) {
    return t?.lastReplyAt || t?.lastMessageAt || t?.updatedAt || t?.createdAt || null;
  }

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setMe(u || null);
      setAuthReady(true);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (authReady && !me) navigate("/login", { replace: true });
  }, [authReady, me, navigate]);

  useEffect(() => {
    if (!authReady || !me || !id) return;

    let unsubTicket = null;
    let unsubMsgs = null;
    let isCancelled = false;

    (async () => {
      try {
        setLoadErr("");

        // Tickets used to live in different collections across versions.
        // Prefer the canonical "tickets" collection, but fallback to "supportTickets"
        // so older tickets still load.
        let foundColl = null;
        for (const c of ["tickets", "supportTickets"]) {
          try {
            const snap = await getDoc(doc(db, c, id));
            if (snap.exists()) {
              foundColl = c;
              break;
            }
          } catch (e) {
            // keep trying other collections
          }
        }

        if (!foundColl) {
          if (!isCancelled) navigate("/support", { replace: true });
          return;
        }

        if (isCancelled) return;
        setTicketColl(foundColl);

        const tRef = doc(db, foundColl, id);
        unsubTicket = onSnapshot(
          tRef,
          (snap) => {
            if (!snap.exists()) {
              navigate("/support", { replace: true });
              return;
            }
            setTicket({ id: snap.id, ...snap.data(), _coll: foundColl });
          },
          (e) => setLoadErr(e?.message || String(e))
        );

        const mq = query(
          collection(db, foundColl, id, "messages"),
          orderBy("createdAt", "asc")
        );
        unsubMsgs = onSnapshot(
          mq,
          (snap) => {
            const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            setMessages(rows);
            setTimeout(() => {
              scrollerRef.current?.scrollTo({
                top: scrollerRef.current.scrollHeight,
                behavior: "smooth",
              });
            }, 0);
          },
          (e) => setLoadErr(e?.message || String(e))
        );
      } catch (e) {
        setLoadErr(e?.message || String(e));
      }
    })();

    return () => {
      isCancelled = true;
      try {
        unsubTicket && unsubTicket();
      } catch (_) {}
      try {
        unsubMsgs && unsubMsgs();
      } catch (_) {}
    };
  }, [authReady, me, id, navigate]);

  async function sendReply(e) {
    e.preventDefault();
    if (!me || !reply.trim() || !ticket) return;
    setBusy(true);
    try {
      const coll = ticket?._coll || ticketColl || "tickets";
      await addDoc(collection(db, coll, id, "messages"), {
        senderId: me.uid,
        senderRole: "user",
        body: reply.trim(),
        createdAt: serverTimestamp(),
      });
      await updateDoc(doc(db, coll, id), {
        updatedAt: serverTimestamp(),
        lastMessageAt: serverTimestamp(),
        lastMessageBy: me.uid,
      });
      setReply("");
    } finally {
      setBusy(false);
    }
  }

  if (!authReady) {
    return (
      <div className="container">
        <div className="header-row">
          <h2>Ticket</h2>
        </div>
      </div>
    );
  }
  if (!me) return null;
  if (!ticket) {
    return (
      <div className="container">
        <div className="header-row">
          <h2>Ticket</h2>
        </div>
        <div className="contact-row">
          <div className="row-mid">Loading…</div>
        </div>
      </div>
    );
  }

  const sk = normalizeStatusKey(ticket);
  const lastTs = bestTs(ticket);
  const lastTsText = lastTs?.toDate ? lastTs.toDate().toLocaleString() : "—";
  const lastRole = String(ticket.lastReplyRole || "").toLowerCase();
  const lastFrom = lastRole === "staff" || lastRole === "support" || lastRole === "admin" ? "Support" : "You";

  return (
    <div className="container">
      <div className="header-row">
        <h2>Ticket</h2>
        <div className="header-actions desktop-only" />
        <Link to="/support" className="btn btn-cancel">
          Back
        </Link>
      </div>

      {loadErr ? (
        <div className="contact-row" style={{ marginTop: 10 }}>
          <div className="row-mid" style={{ color: "#b42318", fontWeight: 700 }}>
            {loadErr}
          </div>
        </div>
      ) : null}

      <div className="contacts-list" style={{ marginTop: 8 }}>
        <div className="contact-row">
          <div className="row-left">
            <div className="thumb placeholder">🎫</div>
          </div>
          <div className="row-mid">
            <div className="title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {ticket.issue || "(no issue)"}
              </span>
              <span className="sub" style={{ paddingLeft: 8, fontWeight: 700, opacity: 0.8 }}>
                {statusLabel(sk)}
              </span>
            </div>
            <div className="sub">
              Last reply {lastFrom} • {lastTsText}
            </div>
          </div>
          <div className="row-right" />
        </div>
      </div>

      <div className="rounded-xl" style={{ border: "1px solid #ececf5", background: "#fff", marginTop: 12 }}>
        <div
          ref={scrollerRef}
          style={{
            maxHeight: "60vh",
            overflow: "auto",
            padding: 12,
            borderBottom: "1px solid #f1f1f7",
          }}
        >
          {messages.length === 0 ? (
            <div className="sub">No messages yet.</div>
          ) : (
            messages.map((m) => (
              <div key={m.id} style={{ padding: "10px 6px", borderBottom: "1px dashed #f1f1f7" }}>
                <div className="sub" style={{ marginBottom: 4 }}>
                  {m.senderRole === "staff" ? "Support" : "You"} •{" "}
                  {m.createdAt?.toDate ? m.createdAt.toDate().toLocaleString() : ""}
                </div>
                <div style={{ whiteSpace: "pre-wrap", color: "#2a0516" }}>{m.body}</div>
              </div>
            ))
          )}
        </div>

        {/* Reply composer */}
        <div style={{ padding: 12 }}>
          <form onSubmit={sendReply} className="grid gap-2">
            <textarea
              className="input"
              style={{ minHeight: 120 }}
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder="Write a reply…"
            />
            <div className="form-actions" style={{ marginTop: 6 }}>
              <button className="btn btn-main" disabled={busy || !reply.trim()}>
                {busy ? "Sending…" : "Send"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
